// THE PLAN CARD — the first workspace thread's plan note (server lane S1: an
// assistant note with `metadata: { kind: 'note', note: 'plan', plan }`, the
// Blueprint snapshot, no words of its own). The shell writes the card in its
// own language: the company and its line, the industry and the team, the apps
// by the App Store's names, the website, the idea (functions/workspaceSetup.js
// workspaceSetupPlanView). Nika 2026-10-25 decision 11: the first thread
// holds the plan, the summary of the site conversation and the first build.
//
// Two places draw it, through one slot (WorkspaceSetupPlanSlot):
//   planOf: 'thread'  the app builder's thread — the plan of the rows it holds
//                     (components/WorkspaceSetupThread.js, above the messages)
//   planOf: 'row'     one main-thread row — the row's own note
//                     (components/AssistantMainThread.js)
// The card itself reads only its own state (every key always present).

// One card: a quiet kicker, the company's name and line, the facts, the apps
// as a row of named chips, the website and the idea.
export const WorkspaceSetupPlan = {
  tag: 'article',
  dataSetupPlan: true,
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'A',
  round: 'radiusCard',
  theme: 'layer',
  color: 'title',
  ariaLabel: (el, s) => (s && s.title) || null,

  SetupPlanKicker: {
    tag: 'span',
    fontSize: 'Z',
    fontWeight: '500',
    lineHeight: '1.3',
    color: 'caption',
    text: (el, s) => (s && s.title) || ''
  },

  SetupPlanName: {
    tag: 'h3',
    margin: '0',
    fontSize: 'A',
    fontWeight: '700',
    lineHeight: '1.3',
    color: 'title',
    overflowWrap: 'anywhere',
    hide: (el, s) => !(s && s.name),
    text: (el, s) => (s && s.name) || ''
  },

  SetupPlanTagline: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'caption',
    overflowWrap: 'anywhere',
    hide: (el, s) => !(s && s.tagline),
    text: (el, s) => (s && s.tagline) || ''
  },

  SetupPlanFacts: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'caption',
    hide: (el, s) => !(s && s.facts),
    text: (el, s) => (s && s.facts) || ''
  },

  SetupPlanAppsLabel: {
    tag: 'span',
    fontSize: 'Z',
    fontWeight: '500',
    lineHeight: '1.3',
    color: 'caption',
    hide: (el, s) => !(s && s.appsText),
    text: (el, s) => (s && s.appsText) || ''
  },

  SetupPlanApps: {
    tag: 'ul',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'Y',
    minWidth: '0',
    hide: (el, s) => !(s && Array.isArray(s.apps) && s.apps.length),
    childExtends: 'WorkspaceSetupPlanApp',
    childrenAs: 'state',
    children: (el, s) => (s && Array.isArray(s.apps) ? s.apps : [])
  },

  SetupPlanSite: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'title',
    overflowWrap: 'anywhere',
    hide: (el, s) => !(s && s.siteText),
    text: (el, s) => (s && s.siteText) || ''
  },

  SetupPlanIdea: {
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    hide: (el, s) => !(s && s.idea),
    SetupPlanIdeaLabel: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.3',
      color: 'caption',
      text: (el, s) => (s && s.ideaLabel) || ''
    },
    SetupPlanIdeaText: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      lineHeight: '1.5',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.idea) || ''
    }
  }
}

// One app of the plan: its glyph and the App Store's name for it.
export const WorkspaceSetupPlanApp = {
  tag: 'li',
  display: 'flex',
  alignItems: 'center',
  gap: 'X',
  minWidth: '0',
  padding: 'X Z',
  round: 'radiusPill',
  theme: 'layer',
  color: 'title',
  fontSize: 'Z',
  lineHeight: '1.3',
  Icon: {
    ariaHidden: 'true',
    name: (el, s) => (s && s.icon) || 'blocks',
    boxSize: 'iconXs',
    flexShrink: '0'
  },
  SetupPlanAppName: {
    tag: 'span',
    minWidth: '0',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.title) || ''
  }
}

// The slot: no card, or one, as a state row (the view resolves its words and
// names when it is built, so a catalogue that lands later redraws it). Hidden
// with no plan, so it takes no gap; on a main-thread row it stands above the
// summary it rides with (`planOf: 'row'` — the row's own spacing below it).
export const WorkspaceSetupPlanSlot = {
  flow: 'y',
  minWidth: '0',
  planOf: 'thread',
  hideDeps: [
    (el, s) => (el.planOf === 'row' ? s && s.metadata : s.root && s.root._ax_chatMessages)
  ],
  hide: (el, s) =>
    !(el.planOf === 'row'
      ? s && s.metadata && s.metadata.kind === 'note' && s.metadata.plan
      : el.call('workspaceSetupPlanOf', s.root && s.root._ax_chatMessages)),
  childExtends: 'WorkspaceSetupPlan',
  childrenAs: 'state',
  childrenDeps: [
    (el, s) => (el.planOf === 'row' ? s && s.metadata : s.root && s.root._ax_chatMessages),
    (el, s) => s.root && s.root._wsAppsCatalog,
    (el, s) => s.root && s.root._wsAppsCatalogLoaded
  ],
  children: (el, s) => {
    const plan =
      el.planOf === 'row'
        ? s && s.metadata && s.metadata.kind === 'note' && s.metadata.plan
          ? s.metadata.plan
          : null
        : el.call('workspaceSetupPlanOf', s.root && s.root._ax_chatMessages)
    const view = plan ? el.call('workspaceSetupPlanView', plan) : null
    return view && view.show ? [{ key: 'setup-plan-card', ...view }] : []
  }
}
